/* eslint-disable @typescript-eslint/prefer-optional-chain */
import { StatusCodes } from 'http-status-codes';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import styled from 'styled-components';
import { ReactSVG } from 'react-svg';

import ButtonComponent from '../../../../../components/common/forms/Button/ButtonComponent';
import SecondaryButton from '../../../../../components/common/forms/Button/SecondaryButton';

import TitleComponent from '../../../../../components/common/forms/Title/TitleComponent';
import SecondaryNavigation from '../../../../../components/common/navigation/SecondaryNavigation';
import WhitePad from '../../../../../components/common/WhitePad/WhitePad';
import InventoryApi from '../../../../../services/api/InventoryApi';
import { OrderProductWithReservedQuantityGetDto } from '~/src/types/Misc';
import _ from 'lodash';
import ProductService from '~/src/services/logic/ProductService';

import copyIcon from '../../../../../static/img/copyIcon.svg';
import updateIcon from '../../../../../static/img/arrowsClockwiseIcon.svg';
import GtinComponent from '~/src/components/common/GtinComponent';
import LinkButton from '~/src/components/common/forms/Button/LinkButton';
import TextInputComponent from '~/src/components/common/forms/TextInput/TextInputComponent';
import BorderlessButton from '~/src/components/common/forms/Button/BorderlessButton';

import xIcon from '../../../../../static/img/xIcon.svg';
import orangeX from '../../../../../static/img/orangeX.svg';
import TableWrapper from '~/src/components/common/forms/TableWrapper/TableWrapper';
import { Colors } from '~/src/scss/common/style';

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

interface ParamProps {
  id: string;
}

export default function Proforma(): React.ReactElement {
  const history = useHistory();
  const { id } = useParams<ParamProps>();

  const [orderProducts, setOrderProducts] = useState<OrderProductWithReservedQuantityGetDto[]>();
  const [textToCopy, setTextToCopy] = useState('');
  const [copyResult, setCopyResult] = useState('');

  const copy = async (): Promise<void> => {
    const type = 'text/plain';
    const blob = new Blob([textToCopy], { type });
    const clipboardItem = new ClipboardItem({ [type]: blob });
    const data = [clipboardItem];

    navigator.clipboard.write(data).then(
      function () {
        setCopyResult('Copied');
      },
      function () {
        setCopyResult("Couldn't copy");
      },
    );
  };

  const updateAllValues = async (): Promise<void> => {
    const result = await InventoryApi.updateProforma(id);
    if (result.status === StatusCodes.OK) {
      const clonedOrderProducts = _.cloneDeep(orderProducts);
      if (clonedOrderProducts) {
        result.data.orderProducts?.forEach((serverOrderProduct) => {
          const matchedOrderProduct = clonedOrderProducts.find(
            (localOrderProduct) => serverOrderProduct.id === localOrderProduct.orderProduct.id,
          );
          if (matchedOrderProduct) {
            matchedOrderProduct.orderProduct.proformaQuantity = serverOrderProduct.proformaQuantity;
          }
        });

        setOrderProducts(clonedOrderProducts);
      }
    }
  };

  const setManualValue = async (productId: string, quantity: number): Promise<void> => {
    const result = await InventoryApi.setManualValueProforma(productId, quantity);
    if (result.status === StatusCodes.OK) {
      const clonedOrderProduct = _.cloneDeep(orderProducts);
      if (clonedOrderProduct) {
        const matchedClonedProduct = clonedOrderProduct.find(
          (localOrderProduct) => localOrderProduct.orderProduct.id === result.data.id,
        );
        if (matchedClonedProduct) {
          matchedClonedProduct.orderProduct.proformaQuantity = result.data.proformaQuantity;
          matchedClonedProduct.orderProduct.proformaQuantityManuallySet = result.data.proformaQuantityManuallySet;
        }
        setOrderProducts(clonedOrderProduct);
      }
    }
  };

  const unsetProforma = async (productId: string, quantity: number): Promise<void> => {
    const result = await InventoryApi.unsetProforma(productId, quantity);
    if (result.status === StatusCodes.OK) {
      const clonedOrderProduct = _.cloneDeep(orderProducts);
      if (clonedOrderProduct) {
        const matchedClonedProduct = clonedOrderProduct.find(
          (localOrderProduct) => localOrderProduct.orderProduct.id === result.data.id,
        );
        if (matchedClonedProduct) {
          matchedClonedProduct.orderProduct.proformaQuantity = result.data.proformaQuantity;
          matchedClonedProduct.orderProduct.proformaQuantityManuallySet = result.data.proformaQuantityManuallySet;
        }
        setOrderProducts(clonedOrderProduct);
      }
    }
  };

  const getProducts = async (): Promise<void> => {
    const result = await InventoryApi.getProforma(id);
    if (result.status === StatusCodes.OK) {
      setOrderProducts(result.data);
    }
  };

  useEffect(() => {
    let ttc = `MNum\tMName\tQuantity\tQuantityCu\n`;
    orderProducts?.forEach((element) => {
      const du = ProductService.getDu(element.orderProduct.product.packagedProducts);
      const cuPerDu = du?.numberSmallerUnits ?? 0;
      ttc += `${element.orderProduct.gtinCu}\t${element.orderProduct.productName}\t${element.orderProduct.proformaQuantity ?? 0
        }\t${(element.orderProduct.proformaQuantity ?? 0) * cuPerDu}\n`;
    });
    setTextToCopy(ttc);
  }, [orderProducts]);

  useEffect(() => {
    if (id) {
      getProducts();
    }
  }, []);

  return (
    <WhitePad>
      <>
        <SecondaryNavigation />
        <TitleComponent
          title="Proforma"
          onLeftHandler={(): void => {
            history.push('/orders-list');
          }}
        />
        <ActionsWrapper>
          <SecondaryButton icon={copyIcon} title="Copy" onClick={copy} />
          <SecondaryButton icon={updateIcon} title="Update" onClick={updateAllValues} />
        </ActionsWrapper>
        &nbsp;
        {copyResult}
        <br />
        <br />
        <br />
        <TableWrapper>
          <thead>
            <tr>
              <th>№</th>
              <th>MNum</th>
              <th>MName</th>
              <th>Ordered</th>
              <th>Reserved</th>
              <th style={{ width: '120px' }}>Proforma Quantity</th>
              <th>Quantity Cu</th>
              <th>Manual</th>
            </tr>
          </thead>
          <tbody>
            {orderProducts &&
              orderProducts.map((item, index) => {
                const idx: string = uuid() as string;
                const du = ProductService.getDu(item.orderProduct.product.packagedProducts);
                const cuPerDu = du?.numberSmallerUnits ?? 0;
                return (
                  <tr key={idx}>
                    <td>{index + 1}</td>
                    <td>
                      <GtinComponent background="#F4F4F4" text="CU" code={item.orderProduct.gtinCu} />
                    </td>
                    <td style={{ textAlign: 'left' }}>{item.orderProduct.productName}</td>
                    <td>
                      <LinkButton
                        variant="link"
                        title={`${item.orderProduct.quantityDu}`}
                        onClick={(): Promise<void> =>
                          setManualValue(item?.orderProduct?.id, item?.orderProduct?.quantityDu)
                        }
                      />
                    </td>
                    <td>
                      <LinkButton
                        variant="link"
                        title={`${item.reservedQuantity}`}
                        onClick={(): Promise<void> =>
                          setManualValue(item?.orderProduct?.id, item?.reservedQuantity)
                        }
                      />
                    </td>
                    <AlignTd>
                      <TextInputComponent
                        max={`${item.orderProduct.quantityDu}`}
                        min="0"
                        height={25}
                        className="form-control"
                        defaultValue={
                          item.orderProduct.proformaQuantity !== null
                            ? item.orderProduct.proformaQuantity.toString()
                            : ''
                        }
                        onBlur={(e): void => {
                          setManualValue(item?.orderProduct?.id, parseInt(e.target.value));
                        }}
                        cols={14}
                      />
                    </AlignTd>
                    <td>{(item.orderProduct.proformaQuantity ?? 0) * cuPerDu}</td>
                    <td>
                      {item.orderProduct.proformaQuantityManuallySet && (
                        <ActionWrapper style={{ display: 'flex' }}>
                          <SecondaryButton
                            title="| Manual"
                            icon={orangeX}
                            onClick={(): Promise<void> =>
                              unsetProforma(
                                item?.orderProduct?.id,
                                item?.reservedQuantity ? item?.reservedQuantity : item?.orderProduct?.quantityDu,
                              )
                            }
                          />
                        </ActionWrapper>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </TableWrapper>
      </>
    </WhitePad>
  );
}

const ActionWrapper = styled.div`
  svg {
    font-size: 9px;
    width: 9px;
    height: 9px;
  }
  button {
    border: 0;
    border-radius: 5px;
    span {
      position: relative;
      padding-right: 5px;
      margin-right: 5px;
      &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        width: 2px;
        background: ${Colors.newOrage};
      }
    }
  }
  button:hover span::before {
    background-color: ${Colors.darkBrown};
  }
`;

const StyledXManual = styled(ReactSVG)`
  display: flex;
  svg {
    width: 9px;
    height: 9px;
    font-size: 9px;
  }
`;

const AlignTd = styled.td`
  vertical-align: middle;
  div {
    margin: 0;
  }
`;
